import React, { useState, useEffect } from "react";
import UserForm from "components/common/UserForm";
import { FiLoader } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UserActionsDropdown from "components/dropdown/UserActionDropdown";
import UserViewModal from "components/modal/UserViewModal";
import "styles/admin.css";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PAGE_SIZE = 6;

const UsersControl = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleView = async (username) => {
    try {
      setViewLoading(true);
      setViewModalOpen(true);

      const res = await api.get(
        `/api/admin/Users/GetByUsername?username=${username}`
      );

      const u = res.data?.data || res.data;

      const formattedUser = {
        id: u.id,
        username: u.username,
        fullName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        phoneNumber: u.phoneNumber || "",
        bio: u.bio || "",
        role: u.role || "User",
        isActive: u.state === "Active",
        imageUrl: u.imgUrl
          ? `${API_BASE}/api/Public/Content/DownloadFile?fileId=${u.imgUrl}`
          : null,
      };

      setSelectedUser(formattedUser);
      console.log(formattedUser);
      
    } catch (err) {
      console.error(err);
      toast.error("Foydalanuvchi ma'lumotlari yuklanmadi");
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.post("/api/admin/Users/GetAll", {
        page: 1,
        pageSize: 200,
      });
      const list = res.data?.rows || res.data || [];
      setAllUsers(list);
      setUsers(list.slice(0, PAGE_SIZE));
      setCurrentPage(1);
      console.log(list);
      
    } catch (err) {
      console.error(err);
      toast.error("Foydalanuvchilar yuklanmadi");
      setAllUsers([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setUsers(allUsers.slice(start, end));
  }, [currentPage, allUsers]);

  const totalPages = Math.ceil(allUsers.length / PAGE_SIZE);

  const handleEdit = async (username) => {
    try {
      const res = await api.get(
        `/api/admin/Users/GetByUsername?username=${username}`
      );
      const u = res.data?.data || res.data;

      setEditingUser({
        id: u.id,
        username: u.username,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        fullName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        phoneNumber: u.phoneNumber || "",
        bio: u.bio || "",
        role: u.role || "User",
        isActive: u.state === "Active",
        imageUrl: u.imgUrl
          ? `${API_BASE}/api/Public/Content/DownloadFile?fileId=${u.imgUrl}`
          : null,
      });
      setShowForm(true);
    } catch {
      toast.error("Ma'lumot yuklanmadi");
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm("Foydalanuvchi faollashtirilsinmi?")) return;
    try {
      await api.put(`/api/admin/Users/Activate/${id}`);
      toast.success("Faollashtirildi");
      fetchUsers();
    } catch {
      toast.error("Xato");
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Foydalanuvchi faolsizlantirilsinmi ?")) return;
    try {
      await api.put(`/api/admin/Users/DeActivate/${id}`);
      toast.success("Faolsizlantirildi");
      fetchUsers();
    } catch {
      toast.error("Xato");
    }
  };

  const handleSubmit = async (formData) => {
    if (!editingUser?.id) return;

    try {
      const textData = {
        FirstName: (formData.fullName.split(" ")[0] || "").trim(),
        LastName: (
          formData.fullName.split(" ").slice(1).join(" ") || ""
        ).trim(),
        PhoneNumber: formData.phoneNumber.trim(),
        Bio: "",
        Role: formData.role,
      };

      await api.put(`/api/admin/Users/Update/${editingUser.id}`, textData);

      if (formData.file && formData.file instanceof File) {
        const imageFormData = new FormData();
        imageFormData.append("File", formData.file);

        await api.put(
          `/api/admin/Users/UpdateUserImg/${editingUser.id}`,
          imageFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      toast.success("Foydalanuvchi muvaffaqiyatli yangilandi!");
      setShowForm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Update xatosi:", err.response?.data || err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.title ||
          "Yangilashda xato yuz berdi"
      );
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} theme="colored" />

      <div className="banner-page-wrapper">
        {showForm ? (
          <UserForm
            initialData={editingUser}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingUser(null);
            }}
          />
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "120px" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FiLoader size={55} />
            </motion.div>
            <p>Yuklanmoqda...</p>
          </div>
        ) : allUsers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "140px", color: "#666" }}>
            <h2>Hozircha foydalanuvchi yo‘q</h2>
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <table
                className="banner-table"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>№</th>
                    <th style={{ width: "100px" }}>Rasm</th>
                    <th>F.I.O</th>
                    <th style={{ width: "200px" }}>Role</th>
                    <th style={{ width: "130px" }}>Status</th>
                    <th style={{ width: "100px" }}>Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {users.map((u, i) => {
                      const globalIndex = (currentPage - 1) * PAGE_SIZE + i + 1;
                      return (
                        <motion.tr
                          key={u.id}
                          layout
                          initial={{ opacity: 0, x: -50 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 50 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          <td style={{ textAlign: "center" }}>{globalIndex}</td>

                          <td style={{ textAlign: "center" }}>
                            {u.imgUrl ? (
                              <img
                                src={`${API_BASE}/api/Public/Content/DownloadFile?fileId=${u.imgUrl}`}
                                alt={u.firstName}
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "3px solid #e0e7ff",
                                  margin: "0 auto",
                                }}
                                onError={(e) =>
                                  (e.target.src =
                                    "https://via.placeholder.com/60/6366f1/ffffff?text=U")
                                }
                              />
                            ) : (
                              <div
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: "50%",
                                  background: "#e0e7ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#6366f1",
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                  margin: "0 auto",
                                }}
                              >
                                {(u.firstName ||
                                  u.username ||
                                  "?")[0].toUpperCase()}
                              </div>
                            )}
                          </td>

                          <td style={{ verticalAlign: "middle" }}>
                            {`${u.firstName || ""} ${
                              u.lastName || ""
                            }`.trim() ||
                              u.username ||
                              "—"}
                          </td>

                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <span
                              style={{
                                padding: "5px 10px",
                                borderRadius: "20px",
                                fontSize: "13px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                background: u.role?.includes("Admin")
                                  ? "#fef2f2"
                                  : u.role?.includes("Teacher")
                                  ? "#f0fdf4"
                                  : u.role?.includes("Student")
                                  ? "#fffbeb"
                                  : "#f3f4f6",
                                color: u.role?.includes("Admin")
                                  ? "#dc2626"
                                  : u.role?.includes("Teacher")
                                  ? "#16a34a"
                                  : u.role?.includes("Student")
                                  ? "#d97706"
                                  : "#6b7280",
                                border: "1px solid",
                                borderColor: u.role?.includes("Admin")
                                  ? "#fecaca"
                                  : u.role?.includes("Teacher")
                                  ? "#86efac"
                                  : u.role?.includes("Student")
                                  ? "#fde68a"
                                  : "#e5e7eb",
                              }}
                            >
                              {u.role || "User"}
                            </span>
                          </td>

                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <span
                              className={`status-badge ${
                                u.state === "Active" ? "active" : "pending"
                              }`}
                              style={{
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "13px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              {u.state === "Active" ? "Faol" : "Faol emas"}
                            </span>
                          </td>

                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <UserActionsDropdown
                              user={u}
                              onEdit={() => handleEdit(u.username)}
                              onActivate={() => handleActivate(u.id)}
                              onDeactivate={() => handleDeactivate(u.id)}
                              refreshList={fetchUsers}
                              onView={() => handleView(u.username)}
                            />
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </motion.div>

            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "30px",
                  padding: "10px",
                }}
              >
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-pagination"
                  style={{
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  ← Oldingi
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className="btn-pagination"
                    style={{
                      background: currentPage === i + 1 ? "#01CC0D" : "#f3f4f6",
                      color: currentPage === i + 1 ? "white" : "#374151",
                      fontWeight: currentPage === i + 1 ? "600" : "500",
                    }}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="btn-pagination"
                  style={{
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Keyingi →
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <UserViewModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        loading={viewLoading}
      />
    </>
  );
};

export default UsersControl;
